import React, { useState, useEffect, useMemo, useRef } from 'react';
import { makeAsk, ERRORS, Card, Button, Notice, Gate, Avatar, asList, selectClass, channelOptions } from './dashboardShared';
import { PARTNER_FEEDS, tidyFeeds } from './PartnersTab';

// #partner: the Partner Dashboard, for people who manage a server TOGΞ has been added to as a partner. They see the
// accounts Cheeks has set for their server (fixed), and choose which channel each posts to and switch it on. The bot
// checks every request: verified in CHΞΞKSCORD, and Manage Server in that server.

export default function PartnerDashboard({ database, discordUser, getToken, onLogin }) {
  const tokenRef = useRef(getToken); // the same `ask` for the life of the page, as in Dashboard.jsx
  tokenRef.current = getToken;
  const ask = useMemo(() => makeAsk(database, () => tokenRef.current()), [database]);
  const [servers, setServers] = useState(null);
  const [current, setCurrent] = useState(null);
  const [view, setView] = useState(null);      // { server, feeds, channels, roles }
  const [drafts, setDrafts] = useState({});    // kind -> { channel, role }
  const [problem, setProblem] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState('');
  const [waiting, setWaiting] = useState([]);  // servers they've added TOGΞ to that are waiting for approval
  const [invite, setInvite] = useState(null);  // the link for adding TOGΞ to a server

  const failed = (answer) => {
    if (answer.ok) return false;
    if (ERRORS[answer.error]) setProblem(answer.error); else setNote({ text: answer.error, ok: false });
    return true;
  };

  const showView = (data) => {
    setView({ ...data, feeds: tidyFeeds(data?.feeds), channels: asList(data?.channels), roles: asList(data?.roles) });
    setDrafts({});
  };

  const loadMine = () => ask('partner.mine').then((answer) => {
    if (failed(answer)) return;
    const list = asList(answer.data?.servers);
    setServers(list);
    setWaiting(asList(answer.data?.waiting));
    setInvite(answer.data?.invite || null);
    setCurrent(c => (c && list.some(s => s.id === c) ? c : list[0]?.id || null));
  });

  useEffect(() => {
    if (!discordUser || !getToken()) return;
    loadMine();
  }, [discordUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!current) return;
    setView(null);
    ask('partner.get', { guild: current }).then((answer) => { if (!failed(answer)) showView(answer.data); });
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (kind, on) => {
    const feed = view.feeds[kind];
    const draft = drafts[kind] || {};
    setBusy(kind); setNote(null);
    const answer = await ask('partner.save', { guild: current, kind, on,
      channel: draft.channel ?? feed.channel ?? '', role: draft.role ?? feed.role ?? '',
      products: draft.products ?? feed.products ?? '' });
    setBusy('');
    if (failed(answer)) return;
    showView(answer.data);
    setNote({ text: answer.data.message, ok: true });
  };

  if (!discordUser) return <Gate title="Partner Dashboard" text="Log in with Discord to manage TOGΞ in your server." onLogin={onLogin} />;
  if (problem === 'login' || !getToken()) return <Gate title="Partner Dashboard" text={ERRORS.login} onLogin={onLogin} />;
  if (problem === 'forbidden') return <Gate title="Partner Dashboard" text="The Partner Dashboard is for TOGΞ partners. Ask Cheeks if you'd like TOGΞ in your server." />;

  // Adding TOGΞ to a server: the whole page when they've no partner server yet, a card under it when they have
  const addCard = (first) => (
    <Card title={first ? null : 'Add TOGΞ to another server'}>
      {first && <p className="text-white text-sm font-bold mb-1">Add TOGΞ to your server</p>}
      <p className="text-zinc-400 text-xs">
        TOGΞ can post in your server when you go live on Whatnot or Twitch, upload products to your Whatnot store, or put up
        a new YouTube video.
        {first ? ' Add it to a server you manage:' : ''}
      </p>
      <ol className="text-zinc-400 text-xs list-decimal pl-5 mt-2 space-y-0.5">
        <li>Press <b className="text-white">Add TOGΞ</b> and pick your server.</li>
        <li>Cheeks gets a request to approve it (TOGΞ does nothing in your server until then). TOGΞ DMs you when it's approved.</li>
        <li>Once Cheeks has set up your accounts, come back here to choose which channels it posts in.</li>
      </ol>
      <div className="flex items-center gap-2 flex-wrap mt-3">
        {invite
          ? <a href={invite} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-md bg-[#5865f2] hover:bg-[#4752c4] text-white text-xs font-semibold">➕ Add TOGΞ ↗</a>
          : <span className="text-zinc-500 text-xs">The invite link isn't available right now.</span>}
        <Button small tone="ghost" onClick={loadMine}>↻ I've added it</Button>
      </div>
      <p className="text-zinc-600 text-[10px] mt-2">You need Manage Server in the server you add it to.</p>
    </Card>
  );

  const waitingCard = waiting.length > 0 && (
    <Card title="Waiting for approval">
      <ul className="space-y-1.5">
        {waiting.map(w => (
          <li key={w.id} className="flex items-center gap-2 text-xs text-zinc-300">
            <Avatar src={w.icon} name={w.name} size={24} />
            <span className="flex-1 min-w-0 truncate">⏳ <b className="text-white">{w.name}</b> is waiting for Cheeks to approve it.</span>
          </li>
        ))}
      </ul>
      <p className="text-zinc-600 text-[10px] mt-2">You'll get a DM from TOGΞ as soon as it's approved, and it'll appear here.</p>
    </Card>
  );

  if (servers && servers.length === 0) {
    return (
      <main className="flex-1">
        <div className="max-w-xl mx-auto px-4 pt-6 pb-10 space-y-4">
          <div className="text-center">
            <p className="text-4xl mb-2">🤝</p>
            <h1 className="text-white text-lg font-bold">Partner Dashboard</h1>
          </div>
          <Notice note={note} />
          {waitingCard}
          {addCard(true)}
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1">
      <div className="max-w-3xl mx-auto px-4 pt-2 pb-10 space-y-4">
        <div className="flex items-center gap-3">
          <Avatar src={view?.server?.icon} name={view?.server?.name || '🤝'} size={40} />
          <div className="min-w-0 flex-1">
            <h1 className="text-white text-lg font-bold leading-tight">Partner Dashboard</h1>
            <p className="text-zinc-400 text-[11px] truncate">{view?.server ? `TOGΞ in ${view.server.name}` : 'Connecting to TOGΞ…'}</p>
          </div>
          {servers && servers.length > 1 && (
            <select value={current || ''} onChange={e => setCurrent(e.target.value)} className={selectClass}>
              {servers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>

        <Notice note={note} />

        <Card>
          <p className="text-zinc-400 text-xs">
            TOGΞ posts in your server when your account goes live, or puts up a new video. Pick the channel for each and
            switch it on. The accounts are set up by Cheeks: ask Cheeks if one needs changing.
          </p>
        </Card>

        {!view ? <Card><p className="text-zinc-500 text-xs">Loading…</p></Card> : PARTNER_FEEDS.map(f => {
          const feed = view.feeds[f.id];
          const draft = drafts[f.id] || {};
          const channel = draft.channel ?? feed.channel ?? '';
          const role = draft.role ?? feed.role ?? '';
          const products = draft.products ?? feed.products ?? '';
          const changed = ['channel', 'role', 'products'].some(k => draft[k] !== undefined && draft[k] !== (feed[k] || ''));
          return (
            <Card key={f.id}>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full" style={{ background: f.color, color: f.text || 'white' }}>{f.label}</span>
                {feed.account
                  ? <a href={feed.account.url} target="_blank" rel="noreferrer" className="text-white text-sm font-bold hover:underline">{feed.account.name}</a>
                  : <span className="text-zinc-500 text-xs">No {f.label} account set up for this server</span>}
                <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${feed.on ? 'bg-green-900/50 text-green-300' : 'bg-zinc-800 text-zinc-400'}`}>
                  {feed.on ? 'ON' : 'OFF'}
                </span>
              </div>
              {feed.account ? (
                <div className="space-y-2">
                  <p className="text-zinc-500 text-[11px]">Posts when {feed.account.name} {f.what}.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className="block">
                      <span className="block text-zinc-400 text-[11px] mb-1">{f.id === 'whatnot' ? 'Going live posts in' : 'Post in'}</span>
                      <select value={channel} onChange={e => setDrafts(d => ({ ...d, [f.id]: { ...d[f.id], channel: e.target.value } }))}
                        className={`w-full ${selectClass}`}>
                        <option value="">Pick a channel</option>
                        {channelOptions(view.channels)}
                      </select>
                    </label>
                    {f.id === 'whatnot' && (
                      <label className="block">
                        <span className="block text-zinc-400 text-[11px] mb-1">New products in (optional)</span>
                        <select value={products} onChange={e => setDrafts(d => ({ ...d, [f.id]: { ...d[f.id], products: e.target.value } }))}
                          className={`w-full ${selectClass}`}>
                          <option value="">Don't post new products</option>
                          {channelOptions(view.channels)}
                        </select>
                      </label>
                    )}
                    {f.id !== 'whatnot' && (
                      <label className="block">
                        <span className="block text-zinc-400 text-[11px] mb-1">Ping a role (optional)</span>
                        <select value={role} onChange={e => setDrafts(d => ({ ...d, [f.id]: { ...d[f.id], role: e.target.value } }))}
                          className={`w-full ${selectClass}`}>
                          <option value="">No ping</option>
                          {view.roles.map(r => <option key={r.id} value={r.id}>@{r.name}</option>)}
                        </select>
                      </label>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {feed.on ? (
                      <>
                        <Button tone="primary" disabled={!changed || !channel || !!busy} onClick={() => save(f.id, true)}>{busy === f.id ? 'Saving…' : 'Save'}</Button>
                        <Button tone="ghost" disabled={!!busy} onClick={() => save(f.id, false)}>Turn off</Button>
                      </>
                    ) : (
                      <Button tone="success" disabled={!channel || !!busy} onClick={() => save(f.id, true)}>{busy === f.id ? 'Saving…' : 'Save and turn on'}</Button>
                    )}
                  </div>
                  {!feed.running && <p className="text-amber-400 text-[11px]">{f.label} posts aren't running on TOGΞ right now.</p>}
                </div>
              ) : (
                <p className="text-zinc-500 text-xs">Ask Cheeks to add your {f.label} account, and it'll appear here to switch on.</p>
              )}
            </Card>
          );
        })}

        {waitingCard}
        {addCard(false)}
      </div>
    </main>
  );
}
